import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, HelpCircle, Loader2, X, TrendingUp, TrendingDown, MapPin, Plus, Sparkles, Pencil, Hammer, CheckCircle2 } from "lucide-react";
import {
  GOODS,
  MARKETS,
  RECIPES,
  HIVE_TYPES,
  BEESWAX,
  PROPOLIS,
  shortName,
  totalItems,
  storageCost,
  priceAt,
  priceClass,
  priceHistory,
  marketEvents,
  eventLabel,
  buyLimit,
  sellLimit,
  levelThreshold,
  unlockedMarkets,
  visibleGoods,
  upgradeCost,
  initialState,
  sanitize,
  buy,
  sell,
  travel,
  tend,
  tendAll,
  collect,
  collectAll,
  renameHive,
  upgradeHive,
  buyHive,
  craft,
  buyStorage,
  buyAuto,
  AUTO_PLANS,
  AUTO_LEVEL,
  treatDisaster,
  resolveDisaster,
  removeHive,
  instantCost,
  instantCostFull,
  treatCost,
  treatCostFull,
  insurancePayout,
  disasterStory,
  OVEN_ITEMS,
  OVEN_COST,
  OVEN_BATCH_COST,
  OVEN_MAX_SLOTS,
  OVEN_MAX_BATCH,
  ovenSlotUpgradeCost,
  buyOven,
  upgradeOvenSlots,
  ovenSlotsAllowed,
  ovenSlotUnlockLevel,
  upgradeOvenBatch,
  startBake,
  netWorth,
  prestigeMult,
  canRetire,
  retirementOffers,
  retireSettlement,
  apiaryValue,
  retire,
  RETIRE_MIN,
  insureHive,
  insuranceCost,
  refreshQuests,
  cancelQuest,
  canCancelQuest,
  questCancelsUsed,
  setQuestSlots,
  questSlotsAllowed,
  questSlotsActive,
  questSlotUnlockLevel,
  QUEST_SLOTS,
  deliverContract,
  deliverableQty,
  QUEST_CANCEL_LIMIT,
  earnAchievements,
  claimDaily,
  ACHIEVEMENTS,
  takeLoan,
  repayLoan,
  creditLimit,
  LOAN_RATE,
  addOrder,
  removeOrder,
  requeen,
  requeenCost,
  applyOffline,
} from "@/lib/honeytycoon";
import mapBg from "../assets/honeytycoon-map.png";

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

// Market goods grouped by kind (rendered in this order, headers shown per group).
const MARKET_GROUPS = [
  { kind: "honey", label: "Honey" },
  { kind: "rare", label: "Rare Honey" },
  { kind: "supply", label: "Supplies" },
  { kind: "craft", label: "Crafted Goods" },
  { kind: "oven", label: "Baked Goods" },
];

// Positions + icons for each of the 15 markets, placed to match the illustrated
// map background (honeytycoon-map.png, viewBox 1200×896). Each sits on the terrain
// that fits its name: the port on the docks, alpine on the snowy peaks, etc.
const MARKET_MAP = [
  { x: 288, y: 425, icon: "🏡" }, // Hometown Market — left-coast village
  { x: 710, y: 255, icon: "🌊" }, // Riverside Bazaar — bend of the mountain river
  { x: 585, y: 195, icon: "⛰️" }, // Mountain Village — mountain foothills
  { x: 1010, y: 380, icon: "⚓" }, // Coastal Port — the harbour docks
  { x: 830, y: 515, icon: "🌻" }, // Golden Valley — the farmland plots
  { x: 675, y: 580, icon: "🦁" }, // Savanna Post — open grassy plain
  { x: 760, y: 100, icon: "🏔️" }, // Alpine Market — highest snowy peaks
  { x: 555, y: 710, icon: "🏜️" }, // Desert Oasis — orange canyon ridges
  { x: 215, y: 690, icon: "🌴" }, // Rainforest Exchange — southern palm peninsula
  { x: 208, y: 100, icon: "❄️" }, // Tundra Outpost — cold northern island
  { x: 112, y: 448, icon: "🏝️" }, // Island Marketplace — isolated west island
  { x: 500, y: 300, icon: "🏛️" }, // Grand Capital — the castle
  { x: 890, y: 320, icon: "🐫" }, // Silk Road Hub — eastern trade hills
  { x: 900, y: 115, icon: "🧊" }, // Arctic Depot — far-north snowy peak
  { x: 905, y: 655, icon: "👑" }, // Royal Court — south-east river delta
];

// Tiny price-history sparkline (green if trending up over the window, red if down).
function Sparkline({ data, w = 48, h = 18 }) {
  const n = data.length;
  if (n < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${((i / (n - 1)) * w).toFixed(1)},${(h - ((v - min) / range) * (h - 2) - 1).toFixed(1)}`).join(" ");
  const up = data[n - 1] >= data[0];
  const color = up ? "#16A34A" : "#DC2626";
  return (
    <svg width={w} height={h} className="shrink-0" style={{ display: "block" }} aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={(w).toFixed(1)} cy={(h - ((data[n - 1] - min) / range) * (h - 2) - 1).toFixed(1)} r="1.8" fill={color} />
    </svg>
  );
}

/* ── SVG imagery ──────────────────────────────────────────────────────────── */
function JarIcon({ good, size = 34, dim = false }) {
  const p = GOODS[good];
  const gid = `ht-jar-${good}-${useId()}`; // unique per instance (avoid duplicate SVG ids)
  return (
    <svg viewBox="0 0 40 48" width={size} height={(size * 48) / 40} style={{ display: "block", opacity: dim ? 0.35 : 1 }}>
      <defs>
        <radialGradient id={gid} cx="38%" cy="30%" r="72%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.75" />
          <stop offset="45%" stopColor={p.color} />
          <stop offset="100%" stopColor={p.color} />
        </radialGradient>
      </defs>
      <rect x="7" y="3" width="26" height="7" rx="2" fill="#C08A2E" />
      <rect x="5" y="10" width="30" height="34" rx="7" fill={`url(#${gid})`} stroke="rgba(0,0,0,0.18)" strokeWidth="1" />
      <rect x="9" y="14" width="5" height="25" rx="2.5" fill="rgba(255,255,255,0.4)" />
      <text x="20" y="30" textAnchor="middle" dominantBaseline="middle" fontSize="15">{p.glyph}</text>
    </svg>
  );
}

function HiveIcon({ kind, health = 100, size = 46 }) {
  const healthy = health >= 30;
  const c = healthy ? "#E8A93A" : "#BBB4A6";
  const c2 = healthy ? "#CE8A2C" : "#9C968A";
  const bands = [
    { y: 34, w: 40, h: 9 },
    { y: 26, w: 36, h: 8 },
    { y: 18, w: 30, h: 8 },
    { y: 11, w: 22, h: 8 },
    { y: 5, w: 13, h: 7 },
  ];
  return (
    <svg viewBox="0 0 48 46" width={size} height={(size * 46) / 48} style={{ display: "block" }}>
      <ellipse cx="24" cy="42" rx="21" ry="3" fill="rgba(0,0,0,0.12)" />
      {bands.map((b, i) => (
        <rect key={i} x={24 - b.w / 2} y={b.y} width={b.w} height={b.h} rx="4" fill={i % 2 ? c2 : c} stroke="rgba(0,0,0,0.1)" />
      ))}
      <rect x="21" y="35" width="6" height="7" rx="2" fill="#5b3a12" />
      {kind === "special" && <text x="37" y="12" fontSize="13">✨</text>}
    </svg>
  );
}

function StallIcon({ size = 56 }) {
  return (
    <svg viewBox="0 0 64 56" width={size} height={(size * 56) / 64} style={{ display: "block" }}>
      <rect x="9" y="24" width="46" height="26" rx="3" fill="#F5E6C8" stroke="#C9A05A" />
      <rect x="9" y="44" width="46" height="6" fill="#C9A05A" />
      <clipPath id="ht-awn"><path d="M5 10 L59 10 L55 26 L9 26 Z" /></clipPath>
      <g clipPath="url(#ht-awn)">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <rect key={i} x={5 + i * 8} y="8" width="8" height="20" fill={i % 2 ? "#E86A5C" : "#FBF6EC"} />
        ))}
      </g>
      <rect x="3" y="7" width="58" height="4" rx="2" fill="#B45309" />
      <text x="32" y="41" textAnchor="middle" fontSize="15">🍯</text>
    </svg>
  );
}

// Guided first-days tutorial. Steps with a `done(s)` predicate auto-advance when
// the player performs the action; info steps advance with a Next button.
const TUTORIAL = [
  { icon: "🐝", title: "Welcome, beekeeper!", body: "Trade honey across 15 markets and run your own hives. This quick tour covers your first few days — you can skip it anytime." },
  { icon: "🛒", title: "Buy low", body: "In the Market below, Buy a good while it's cheap (green ▼). Each row's sparkline shows its 10-day trend.", done: (s) => (s.bought || []).some((x) => x > 0) },
  { icon: "🐝", title: "Tend your hive", body: "Open the 🐝 Apiary tab (left) and Tend your hive — it makes honey you can Collect and sell.", done: (s) => s.hives.some((h) => h.tends > 0) },
  { icon: "🗺️", title: "A trip = a new day", body: "Open 🗺️ Travel and pick a market. Every trip advances a day: prices shift and hives can be tended again.", done: (s) => s.day > 1 },
  { icon: "💰", title: "Sell high", body: "Back in the Market, Sell when the price is high (red ▲). Selling above the base price is how you profit.", done: (s) => (s.stats && s.stats.sales) > 0 },
  { icon: "🎯", title: "Set your sights", body: "🎯 Goals has contracts & achievements that pay out; 🏦 Bank has loans & auto-sell orders. Grow your net worth to retire for a permanent bonus!" },
  { icon: "🎓", title: "You're ready!", body: "That's the loop: buy low, tend & craft, sell high, chase goals. Tap the ❓ Help tab anytime for a refresher on any section." },
];

// Per-section reference shown in the ❓ Help drawer tab.
const HELP = [
  { icon: "🛒", title: "Market & Trading", points: ["Prices trend day to day — the sparkline shows the last 10 days. Buy on a dip (green ▼), sell on a peak (red ▲).", "Each good has separate daily limits for buying and selling.", "Watch the event banner: 🎉 demand spikes (sell high) and 📦 surpluses (buy cheap); tomorrow's event is previewed."] },
  { icon: "🗺️", title: "Travel", points: ["Every trip to a market advances one day (revisit your current market to just wait a day).", "Markets unlock as you level up. Your current market glows on the map; grayed ones are locked."] },
  { icon: "🐝", title: "Apiary — your hives", points: ["Tend a hive daily (small fee) to make honey, plus beeswax/propolis/honeycomb over time. Collect it to sell.", "Each hive has a Queen (productivity + disease resistance) — Requeen for a fresh random lineage.", "From Level 10, disasters can strike: treat daily or resolve instantly, or the hive is lost. Insure a hive for a payout if it's lost.", "From Level 7, buy Auto-tend to tend & collect every hive automatically for a set number of days."] },
  { icon: "🔨", title: "Workshop — crafting", points: ["From Level 5, craft goods from blends of honey, wax and propolis — a new recipe every 3 levels.", "Crafted goods sell for far more than their ingredients."] },
  { icon: "🔥", title: "Oven", points: ["From Level 10, buy an oven ($150k) to bake high-value treats over a few days.", "Add another oven every 5 levels (Lv 15, 20, 25…) to bake several items at once, and upgrade Batch for bigger yields per bake.", "The oven bank shows each oven: baking ones display the item + days left, idle ones are empty, and ovens you haven't unlocked yet are greyed out."] },
  { icon: "🎯", title: "Goals", points: ["Contracts: deliver a good to a market by a deadline for a bonus (sell it there). Bigger, better-paying ones appear as you level up.", "You start with 1 contract slot; a 2nd unlocks at Level 5 and a 3rd at Level 7. Use \"Run at once\" to choose how many to juggle — set it lower if you'd rather focus on one.", "Deliver within 3 days of posting for a speed bonus of up to +25% extra.", "Cancel a contract you don't want (up to 6 per 30 days) — that slot stays empty for 3–10 days before a new one posts.", "Achievements pay one-time cash rewards for milestones.", "Retire at $250k net worth to restart with a permanent +15% sale bonus (stacks) and keep your achievements.", "Retiring sells your apiary: three buyers bid 35–85% of what you invested, and the lower the cash offer the bigger the crate of honey & supplies thrown in. Pick whichever suits your next run."] },
  { icon: "🏦", title: "Bank", points: ["Borrow against your credit line — interest is 3%/day, so repay promptly. Debt lowers your net worth.", "Standing auto-sell orders sell a good automatically each day the price clears your floor."] },
  { icon: "📦", title: "Pantry & storage", points: ["Your pantry holds a limited number of items — buy Storage (+100 each, cost +25% per purchase) to hold more.", "Daily logins and time away both grant honey/cash bonuses — check back often!"] },
];

/* ── page ─────────────────────────────────────────────────────────────────── */
export default function HoneyTycoon() {
  // Standalone arcade builds set window.__ARCADE_STANDALONE__ (no app sidebar):
  // the left tab rail and drawer sit flush-left instead of after the 16rem sidebar.
  const standalone = typeof window !== "undefined" && window.__ARCADE_STANDALONE__ === true;
  const railLeft = standalone ? "md:left-0" : "md:left-64";
  const mainPadL = standalone ? "md:pl-20" : "md:pl-5";
  const [loaded, setLoaded] = useState(false);
  const [s, setS] = useState(null);
  const [amount, setAmount] = useState(1);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [panel, setPanel] = useState(null); // "travel" | "goals" | "apiary" | "workshop" | "oven" | "bank" | null
  const [orderGood, setOrderGood] = useState(0);
  const [orderPrice, setOrderPrice] = useState("");
  const lastPanel = useRef(null);
  if (panel) lastPanel.current = panel; // keep content mounted while it slides closed
  const shownPanel = panel || lastPanel.current;

  useEffect(() => {
    let cancelled = false;
    api
      .get("/games/honeytycoon")
      .then((r) => {
        if (cancelled) return;
        let st = refreshQuests(sanitize(r.data) || initialState());
        // offline production since the last save (only for existing saves)
        const off = r.data && r.data.lastSeen ? applyOffline(st, Date.now() - r.data.lastSeen) : null;
        const today = new Date().toISOString().slice(0, 10);
        const daily = claimDaily(st, today); // mutates st (adds cash + streak) if a new day
        setS(st);
        if (off) toast.success(`🐝 Welcome back! Your hives made ${off.items} honey over ${off.days} day${off.days > 1 ? "s" : ""} away.`);
        if (daily) toast.success(`🗓️ Daily bonus — ${daily.streak}-day streak! +${fmt(daily.reward)}`);
      })
      .catch(() => setS(refreshQuests(initialState())))
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveTimer = useRef(null);
  useEffect(() => {
    if (!loaded || !s) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/games/honeytycoon", { ...s, lastSeen: Date.now() }).catch(() => {}); // stamp for offline production
    }, 500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [loaded, s]);

  const setTut = useCallback((v) => setS((prev) => (prev ? { ...prev, tut: v } : prev)), []);

  // Auto-advance the tutorial once the current step's action is completed.
  useEffect(() => {
    if (!loaded || !s || s.tut < 0 || s.tut >= TUTORIAL.length) return;
    const step = TUTORIAL[s.tut];
    if (step.done && step.done(s)) {
      const next = s.tut + 1;
      setS((prev) => ({ ...prev, tut: next }));
      if (next >= TUTORIAL.length) toast.success("🎓 Tutorial complete — happy beekeeping!");
    }
  }, [loaded, s]);

  const apply = useCallback((res) => {
    if (!res) return;
    let earned = [];
    if (res.ok) {
      earned = earnAchievements(res.state); // mutates the fresh clone (adds cash + ids)
      res.state = refreshQuests(res.state); // keep the contract board full
    }
    setS(res.state);
    if (res.note) (res.ok ? toast.success : toast.error)(res.note);
    if (res.leveledTo) toast.success(`⭐ Level ${res.leveledTo}! A new market may be open.`);
    if (res.autoSold && res.autoSold.length) res.autoSold.forEach((a) => toast.success(`🤝 Auto-sold ${a.n} ${GOODS[a.good].name} for ${fmt(a.rev)}`));
    if (res.contract) toast.success(`📜 Contract complete! +${fmt(res.contract.reward)}${res.contract.quick ? ` (incl. ⚡ +${fmt(res.contract.quick)} speed bonus)` : ""}`);
    if (res.questExpired) toast.error("📜 A contract expired — a new one is posted.");
    if (res.insPending && res.insPending.length) res.insPending.forEach((x) => toast.success(`🛡️ ${x.name} was insured — ${fmt(x.payout)} waiting. Remove the hive to collect.`, { duration: 12000 }));
    if (res.insPaid) toast.success(`🛡️ Insurance paid out for ${res.insPaid.name}: +${fmt(res.insPaid.payout)}`);
    if (res.struck && res.struck.length) res.struck.forEach((d) => toast.error(`🚨 ${d.name}: ${d.event}! Treat it or lose the hive.`, { duration: 12000 }));
    if (res.spread && res.spread.length) res.spread.forEach((d) => toast.error(`🚨 Disaster spread to ${d.name} — ${d.event}!`, { duration: 12000 }));
    if (res.baked && res.baked.length) res.baked.forEach((b) => toast.success(`🔥 ${b.qty} ${GOODS[b.good].name} out of the oven!`));
    if (res.cured && res.cured.length) res.cured.forEach((n) => toast.success(`${n} recovered! 🐝`));
    if (res.lost && res.lost.length) res.lost.forEach((d) => toast.error(`${d.name} was lost to ${d.event} 💀`));
    if (res.abandoned && res.abandoned.length) toast.error(`${res.abandoned.length} hive${res.abandoned.length > 1 ? "s were" : " was"} abandoned! 🐝`);
    earned.forEach((a) => toast.success(`🏅 Achievement: ${a.name}! +${fmt(a.reward)}`));
  }, []);

  const qtyFor = amount === "max" ? 1e9 : amount;

  const resetGame = useCallback(() => {
    if (!window.confirm("Reset your game to Day 1 (one hive, $2,000)? This erases your tycoon progress.")) return;
    const fresh = initialState();
    setS(fresh);
    api.put("/games/honeytycoon", fresh).catch(() => {}); // persist the reset immediately (this user only)
    toast.success("Fresh start — Day 1, one hive! 🍯");
  }, []);

  // Retiring is a two-step flow: gather three bids, then accept one. The offers
  // are generated once here (not during render) so they can't be re-rolled.
  const [retireOffers, setRetireOffers] = useState(null);
  const [payLoanOnRetire, setPayLoanOnRetire] = useState(true);
  const retireGame = useCallback(() => {
    if (!s || !canRetire(s)) return;
    setPayLoanOnRetire(true);
    setRetireOffers(retirementOffers(s));
  }, [s]);

  const acceptRetireOffer = useCallback(
    (offer) => {
      if (!s || !canRetire(s)) return;
      const { paid, debt } = retireSettlement(offer, s.loan, payLoanOnRetire);
      const loanLine = s.loan > 0 ? (paid > 0 ? ` ${fmt(paid)} goes to your loan${debt > 0 ? `, and ${fmt(debt)} of debt carries over` : " — clearing it"}.` : ` Your ${fmt(debt)} loan carries over to the new run.`) : "";
      if (!window.confirm(`Sell the apiary for ${fmt(offer.cash)}${offer.goods.length ? " plus the crate of stock" : ""} and retire?${loanLine} You restart at Day 1, but keep a permanent +15% sale bonus (stacking) and all your achievements.`)) return;
      const res = retire(s, offer, { payLoan: payLoanOnRetire });
      if (!res.ok) return;
      setRetireOffers(null);
      setS(res.state);
      api.put("/games/honeytycoon", res.state).catch(() => {});
      toast.success(res.note);
    },
    [s, payLoanOnRetire],
  );

  const commitRename = useCallback(
    (id) => {
      apply(renameHive(s, id, editName));
      setEditing(null);
    },
    [apply, s, editName],
  );

  if (!loaded || !s) {
    return (
      <div className="grid place-items-center py-24 text-stone-400">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const threshold = levelThreshold(s.level);
  const pct = Math.min(100, Math.round((s.cash / threshold) * 100));
  const unlocked = new Set(unlockedMarkets(s.level));
  const goods = visibleGoods(s.level);
  const anyLowHive = s.hives.some((h) => h.health < 30);

  // Travel map — lives in the slide-out drawer; picking a market auto-closes it.
  const travelMap = (
    <div className="rounded-2xl border border-stone-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold text-stone-800">Travel</h2>
        <span className="flex items-center gap-1 text-xs text-stone-400"><MapPin className="h-3 w-3 text-amber-500" /> {MARKETS[s.market].name} · each visit = 1 day</span>
      </div>
      <div className="overflow-hidden rounded-xl border border-sky-100">
        <svg viewBox="0 0 1200 896" width="100%" style={{ display: "block" }} data-testid="ht-map">
          <image href={mapBg} x="0" y="0" width="1200" height="896" preserveAspectRatio="xMidYMid meet" />
          {MARKETS.map((m, i) => {
            const loc = MARKET_MAP[i];
            const open = unlocked.has(i);
            const here = i === s.market;
            return (
              <g key={i} onClick={open ? () => { apply(travel(s, i)); setPanel(null); } : undefined} style={{ cursor: open ? "pointer" : "default" }} data-testid={`ht-market-${i}`}>
                <title>{open ? `${m.name}${here ? " (here)" : ""}` : `${m.name} — unlocks Lv ${m.unlock}`}</title>
                {here && <circle cx={loc.x} cy={loc.y} r="40" fill="#FCD34D" opacity="0.5" className="animate-pulse" />}
                <circle cx={loc.x} cy={loc.y} r="26" fill={here ? "#FDE68A" : open ? "#ffffff" : "#E7E5E4"} stroke={here ? "#D97706" : open ? "#F59E0B" : "#CBC9C4"} strokeWidth={here ? 6 : 3.5} opacity={open ? 0.96 : 0.85} />
                <text x={loc.x} y={loc.y + 12} textAnchor="middle" fontSize="34" opacity={open ? 1 : 0.35} style={{ pointerEvents: "none" }}>{loc.icon}</text>
                {!open && <text x={loc.x + 20} y={loc.y - 15} textAnchor="middle" fontSize="26" style={{ pointerEvents: "none" }}>🔒</text>}
                <text x={loc.x} y={loc.y + 52} textAnchor="middle" fontSize="24" fontWeight={here ? 700 : 600} fill={open ? (here ? "#B45309" : "#3F3A34") : "#6B6560"} stroke="#ffffff" strokeWidth="4" paintOrder="stroke" strokeLinejoin="round" style={{ pointerEvents: "none" }}>{open ? m.name.split(" ")[0] : `Lv ${m.unlock}`}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );

  // Goals drawer: contract, legacy/prestige, achievements.
  const goalsPanel = (
    <>
      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-stone-800">📜 Contracts</h2>
          <span className="text-xs text-stone-500" title={`Up to ${QUEST_CANCEL_LIMIT} cancellations every 30 days`}>{QUEST_CANCEL_LIMIT - questCancelsUsed(s)} / {QUEST_CANCEL_LIMIT} cancels left</span>
        </div>
        {/* How many contracts to run at once — capped by what your level has unlocked. */}
        <div className="mb-2.5 flex items-center gap-2" data-testid="ht-contract-slots">
          <span className="text-xs text-stone-500">Run at once:</span>
          <div className="flex overflow-hidden rounded-lg border border-stone-200">
            {Array.from({ length: QUEST_SLOTS }).map((_, idx) => {
              const n = idx + 1;
              const unlocked = n <= questSlotsAllowed(s.level);
              const selected = n === questSlotsActive(s);
              return (
                <button
                  key={n}
                  disabled={!unlocked}
                  onClick={() => apply(setQuestSlots(s, n))}
                  title={unlocked ? `Run ${n} contract${n > 1 ? "s" : ""} at a time` : `Unlocks at Level ${questSlotUnlockLevel(n)}`}
                  className={`h-7 w-8 border-l border-stone-200 text-xs font-semibold first:border-l-0 ${selected ? "bg-amber-500 text-white" : unlocked ? "bg-white text-stone-600 hover:bg-amber-50" : "cursor-not-allowed bg-stone-50 text-stone-300"}`}
                  data-testid={`ht-contract-slots-${n}`}
                >
                  {unlocked ? n : "🔒"}
                </button>
              );
            })}
          </div>
          {questSlotsAllowed(s.level) < QUEST_SLOTS && (
            <span className="text-[11px] text-stone-400">next at Lv {questSlotUnlockLevel(questSlotsAllowed(s.level) + 1)}</span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="ht-contracts">
          {(s.quests || []).map((q, i) => {
            if (q && Number.isFinite(q.good)) {
              const daysLeft = q.dueDay - s.day;
              const pct = Math.min(100, (q.progress / q.qty) * 100);
              const quickLeft = q.quickDay - s.day;
              const here = q.market === s.market;
              const deliverN = deliverableQty(s, i);
              const deliverTitle = here
                ? deliverN > 0 ? `Deliver ${deliverN} from your pantry now — ignores the market's daily sell cap` : `No ${GOODS[q.good].name} on hand to deliver`
                : `Travel to ${MARKETS[q.market].name} to deliver`;
              return (
                <div key={i} className={`rounded-xl border p-3 ${daysLeft < 0 ? "border-red-200 bg-red-50/40" : "border-amber-200 bg-amber-50/40"}`} data-testid={`ht-contract-${i}`}>
                  <div className="text-sm font-medium text-stone-800">Deliver {q.qty} {GOODS[q.good].glyph} {GOODS[q.good].name} to {MARKETS[q.market].name}</div>
                  <div className="mt-0.5 text-xs text-stone-500">reward <b className="text-amber-700">{fmt(q.reward)}</b> · {daysLeft >= 0 ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : "overdue"}{here ? " · sell here" : ""}</div>
                  {quickLeft >= 0 && <div className="mt-0.5 text-[11px] font-medium text-emerald-600">⚡ +{Math.round(q.quickPct * 100)}% speed bonus if delivered by day {q.quickDay}</div>}
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-stone-100"><div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} /></div>
                  <div className="mt-1 text-right text-[11px] tabular-nums text-stone-500">{q.progress} / {q.qty}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={() => apply(deliverContract(s, i))} disabled={deliverN < 1} title={deliverTitle} className="flex-1 rounded-md bg-amber-500 px-2 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400" data-testid={`ht-deliver-quest-${i}`}>{deliverN > 0 ? `Deliver ${deliverN}` : "Deliver"}</button>
                    <button onClick={() => apply(cancelQuest(s, i))} disabled={!canCancelQuest(s)} title={canCancelQuest(s) ? "Cancel this contract" : `Cancel limit reached (${QUEST_CANCEL_LIMIT} per 30 days)`} className="shrink-0 rounded-md border border-stone-200 px-2.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40" data-testid={`ht-cancel-quest-${i}`}>Cancel</button>
                  </div>
                </div>
              );
            }
            const wait = q && q.cooldownUntil != null ? q.cooldownUntil - s.day : 0;
            return (
              <div key={i} className="rounded-xl border border-dashed border-stone-200 p-3 text-center text-xs text-stone-400" data-testid={`ht-contract-${i}`}>
                {wait > 0 ? `New contract posts in ${wait} day${wait === 1 ? "" : "s"}` : "Posting a new contract…"}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-stone-800">🏆 Legacy</h2>
          <span className="text-xs text-stone-500">Apprentice #{s.prestige} · +{Math.round((prestigeMult(s) - 1) * 100)}% sales</span>
        </div>
        <div className="text-xs text-stone-500">Net worth: <b className="text-stone-800">{fmt(netWorth(s))}</b> · retire at {fmt(RETIRE_MIN)} for a permanent bonus.</div>
        {!retireOffers ? (
          <Button className="mt-2 w-full" size="sm" disabled={!canRetire(s)} onClick={retireGame} data-testid="ht-retire">Retire &amp; pass on the apiary (+15% sales)</Button>
        ) : (
          <div className="mt-2" data-testid="ht-retire-offers">
            <div className="mb-1.5 text-xs text-stone-500">
              Three buyers bid for your apiary (worth <b className="text-stone-700">{fmt(apiaryValue(s))}</b>). A lower cash offer comes with a bigger crate of stock — pick one:
            </div>
            {s.loan > 0 && (
              <label className="mb-2 flex cursor-pointer items-start gap-2 rounded-xl border border-sky-200 bg-sky-50/60 p-2.5" data-testid="ht-retire-payloan">
                <input type="checkbox" checked={payLoanOnRetire} onChange={(e) => setPayLoanOnRetire(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-sky-600" />
                <span className="text-[11px] leading-snug text-sky-900">
                  Put the sale proceeds toward my <b>{fmt(s.loan)}</b> bank loan first.
                  <span className="mt-0.5 block text-sky-700/80">Any debt the sale doesn&apos;t cover carries into the new run — where it keeps accruing {Math.round(LOAN_RATE * 100)}%/day interest.</span>
                </span>
              </label>
            )}
            <div className="space-y-2">
              {retireOffers.map((o, i) => {
                const settle = retireSettlement(o, s.loan, payLoanOnRetire);
                return (
                  <div key={i} className="rounded-xl border border-amber-200 bg-amber-50/40 p-2.5" data-testid={`ht-retire-offer-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-stone-800">{fmt(o.cash)} <span className="text-xs font-normal text-stone-500">cash ({Math.round(o.pct * 100)}%)</span></div>
                      <Button size="sm" className="h-7 px-2.5 text-xs" onClick={() => acceptRetireOffer(o)} data-testid={`ht-retire-accept-${i}`}>Accept</Button>
                    </div>
                    {o.goods.length > 0 ? (
                      <div className="mt-1 text-[11px] text-stone-600">
                        + crate worth <b className="text-amber-700">{fmt(o.goodsValue)}</b>: {o.goods.map((c) => `${c.qty} ${GOODS[c.good].glyph}`).join(" · ")}
                      </div>
                    ) : (
                      <div className="mt-1 text-[11px] text-stone-400">no stock included</div>
                    )}
                    {s.loan > 0 && (
                      <div className="mt-1 text-[11px] font-medium text-sky-700">
                        {settle.paid > 0 && <>🏦 −{fmt(settle.paid)} to the loan · </>}
                        start with <b>{fmt(settle.cash)}</b> cash
                        {settle.debt > 0 ? <> · <span className="text-red-600">{fmt(settle.debt)} debt carried</span></> : <> · <span className="text-emerald-600">debt clear</span></>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => setRetireOffers(null)} data-testid="ht-retire-cancel">Cancel</Button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-stone-800">🏅 Achievements</h2>
          <span className="text-xs text-stone-500">{s.ach.length} / {ACHIEVEMENTS.length}</span>
        </div>
        <div className="space-y-1.5">
          {ACHIEVEMENTS.map((a) => {
            const done = s.ach.includes(a.id);
            return (
              <div key={a.id} className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${done ? "border-amber-200 bg-amber-50/50" : "border-stone-100"}`}>
                <span className={`text-lg ${done ? "" : "opacity-30 grayscale"}`}>{a.icon}</span>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium ${done ? "text-stone-800" : "text-stone-400"}`}>{a.name}</div>
                  <div className="text-[11px] text-stone-400">{a.desc}</div>
                </div>
                <span className={`whitespace-nowrap text-xs font-semibold ${done ? "text-amber-600" : "text-stone-300"}`}>{done ? "✓ earned" : fmt(a.reward)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  // Bank drawer: loans + standing auto-sell orders.
  const bankPanel = (
    <>
      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-stone-800">🏦 Loans</h2>
          <span className="text-xs text-stone-500">{Math.round(LOAN_RATE * 100)}%/day interest</span>
        </div>
        <div className="text-xs text-stone-500">Owed: <b className={s.loan > 0 ? "text-red-600" : "text-stone-800"}>{fmt(s.loan)}</b> · credit line {fmt(creditLimit(s))}</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[10000, 50000].map((amt) => (
            <Button key={amt} size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={creditLimit(s) - s.loan < 1} onClick={() => apply(takeLoan(s, amt))} data-testid={`ht-borrow-${amt}`}>Borrow {fmt(amt)}</Button>
          ))}
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={creditLimit(s) - s.loan < 1} onClick={() => apply(takeLoan(s, creditLimit(s) - s.loan))}>Borrow max</Button>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={s.loan < 1 || s.cash < 1} onClick={() => apply(repayLoan(s, 10000))}>Repay {fmt(10000)}</Button>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={s.loan < 1 || s.cash < 1} onClick={() => apply(repayLoan(s, s.loan))}>Repay all</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <h2 className="mb-1 font-display text-lg font-semibold text-stone-800">🤝 Auto-sell orders</h2>
        <p className="mb-2 text-xs text-stone-500">Each day you travel, these sell automatically at your current market once the price clears the floor.</p>
        {s.orders.length > 0 ? (
          <div className="mb-2 space-y-1">
            {s.orders.map((o) => (
              <div key={o.good} className="flex items-center gap-2 rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm">
                <span>{GOODS[o.good].glyph}</span>
                <span className="min-w-0 flex-1 truncate">{GOODS[o.good].name} · sell at {fmt(o.minPrice)}+</span>
                <button className="text-xs text-red-500 hover:underline" onClick={() => apply(removeOrder(s, o.good))} data-testid={`ht-order-remove-${o.good}`}>remove</button>
              </div>
            ))}
          </div>
        ) : (
          <p className="mb-2 text-xs text-stone-400">No orders yet.</p>
        )}
        <div className="flex items-center gap-1.5">
          <select value={orderGood} onChange={(e) => setOrderGood(+e.target.value)} className="min-w-0 flex-1 rounded-lg border border-stone-200 bg-white px-2 py-1.5 text-sm">
            {goods.map((i) => (
              <option key={i} value={i}>{GOODS[i].name}</option>
            ))}
          </select>
          <input type="number" min="1" value={orderPrice} onChange={(e) => setOrderPrice(e.target.value)} placeholder="min $" className="w-20 rounded-lg border border-stone-200 px-2 py-1.5 text-sm" data-testid="ht-order-price" />
          <Button size="sm" className="h-8" disabled={!(+orderPrice > 0)} onClick={() => { apply(addOrder(s, orderGood, +orderPrice)); setOrderPrice(""); }} data-testid="ht-order-add">Add</Button>
        </div>
      </div>
    </>
  );

  // Help drawer: per-section reference + replay the tutorial.
  const helpPanel = (
    <div className="space-y-3">
      {HELP.map((sec) => (
        <div key={sec.title} className="rounded-2xl border border-stone-200 bg-white p-4">
          <h2 className="mb-1.5 flex items-center gap-2 font-display text-base font-semibold text-stone-800"><span className="text-lg">{sec.icon}</span> {sec.title}</h2>
          <ul className="list-disc space-y-1 pl-5 text-sm text-stone-600">
            {sec.points.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      ))}
      <Button variant="outline" className="w-full" onClick={() => { setTut(0); setPanel(null); }} data-testid="ht-replay-tutorial">▶ Replay the tutorial</Button>
    </div>
  );

  // Mobile section switcher: a horizontal row of tab tiles (md:hidden). Shown at
  // the top of the main panel (to open a section) and again atop the open drawer
  // (to switch), replacing the desktop left rail which squished on small screens.
  const mobileTabs = () => (
    <div className="flex gap-1 overflow-x-auto md:hidden" data-testid="ht-mobile-tabs">
      {[
        { key: "travel", icon: "🗺️", short: "Travel" },
        { key: "apiary", icon: "🐝", short: "Apiary" },
        { key: "workshop", icon: "🔨", short: "Craft" },
        { key: "oven", icon: "🔥", short: "Oven" },
        { key: "goals", icon: "🎯", short: "Goals" },
        { key: "bank", icon: "🏦", short: "Bank" },
        { key: "help", icon: "❓", short: "Help" },
      ].map((p) => {
        const activeTab = panel === p.key;
        let badge = null;
        if (p.key === "apiary") {
          const diseased = s.hives.filter((h) => h.disaster && !h.dead).length;
          const needsTending = s.hives.some((h) => !h.dead && !h.disaster && h.lastTendDay < s.day);
          const auto = s.autoDays > 0;
          const text = diseased > 0 ? "🚨" : auto ? s.autoDays : needsTending ? "!" : "✓";
          const cls = diseased > 0 ? "animate-pulse bg-red-600" : auto || !needsTending ? "bg-emerald-500" : "bg-red-500";
          badge = <span className={`absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full px-1 text-[9px] font-bold leading-none text-white shadow ${cls}`}>{text}</span>;
        }
        return (
          <button
            key={p.key}
            onClick={() => setPanel(p.key)}
            data-testid={`ht-tab-${p.key}`}
            className={`relative flex min-w-[44px] flex-1 basis-0 flex-col items-center gap-0.5 rounded-xl border px-1 py-1.5 text-[10px] font-medium shadow-sm ${activeTab ? "border-amber-400 bg-amber-100 text-amber-800" : "border-amber-200 bg-white/95 text-stone-600 hover:bg-amber-50"}`}
          >
            {badge}
            <span className="text-base leading-none">{p.icon}</span>
            <span className="leading-none">{p.short}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={`space-y-5 pl-5 ${mainPadL}`} data-testid="honeytycoon-page">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/app/games" className="text-stone-500 hover:text-amber-600" data-testid="honeytycoon-back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">🍯 Honey Tycoon</h1>
            <p className="text-sm text-stone-500">
              Level <span className="font-semibold text-stone-700">{s.level}</span> · Day{" "}
              <span className="font-semibold text-stone-700">{s.day}</span> · Best {fmt(s.high)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={resetGame} data-testid="honeytycoon-reset">Reset game</Button>
          <Button variant="ghost" size="icon" onClick={() => setPanel("help")} data-testid="honeytycoon-help"><HelpCircle className="h-5 w-5" /></Button>
        </div>
      </div>

      {/* mobile section switcher (opener) — sticks to the top so it's always
          reachable; the desktop left rail handles this on md+. */}
      <div className="sticky top-0 z-30 -mt-1 bg-stone-50/95 py-1.5 backdrop-blur md:hidden">
        {mobileTabs()}
      </div>

      {/* persistent disaster alert — stays pinned at the top until every stricken
          hive is treated/cured, so it can't be buried by market toasts. */}
      {(() => {
        const diseased = s.hives.filter((h) => h.disaster && !h.dead);
        if (!diseased.length) return null;
        return (
          <button
            onClick={() => setPanel("apiary")}
            className="flex w-full items-center gap-3 rounded-2xl border-2 border-red-400 bg-red-50 p-3 text-left shadow-md ring-1 ring-red-200"
            data-testid="ht-disaster-banner"
          >
            <span className="animate-pulse text-2xl leading-none">🚨</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-red-700">
                {diseased.length} hive{diseased.length > 1 ? "s" : ""} in disaster — treat now or lose {diseased.length > 1 ? "them" : "it"}!
              </div>
              <div className="truncate text-xs text-red-600">
                {diseased.map((h) => `${h.name}: ${h.disaster} (${h.disDaysLeft}d left)`).join(" · ")}
              </div>
            </div>
            <span className="shrink-0 rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white">Open Apiary →</span>
          </button>
        );
      })()}

      {/* bank + progress */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 p-4">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-amber-700/70">Bank balance</div>
            <div className="font-display text-3xl font-bold tabular-nums text-stone-800" data-testid="honeytycoon-cash">{fmt(s.cash)}</div>
          </div>
          <div className="text-right text-sm text-stone-500">
            <div>Next level at</div>
            <div className="font-semibold text-stone-700">{fmt(threshold)}</div>
          </div>
        </div>
        <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-white/70">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* pantry */}
      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg font-semibold text-stone-800">Pantry</h2>
          <div className="flex items-center gap-2">
            <span className={`text-sm tabular-nums ${totalItems(s) >= s.cap ? "font-semibold text-red-600" : "text-stone-500"}`} data-testid="ht-capacity">
              {totalItems(s)} / {s.cap} items
            </span>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={s.cash < storageCost(s)} onClick={() => apply(buyStorage(s))} data-testid="ht-buy-storage">+100 storage · {fmt(storageCost(s))}</Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3" data-testid="ht-pantry">
          {goods.map((i) => {
            const owned = s.inv[i];
            return (
              <div key={i} className={`flex w-[74px] flex-col items-center rounded-xl border p-2 ${owned > 0 ? "border-amber-200 bg-amber-50/40" : "border-stone-100"}`} title={GOODS[i].name}>
                <JarIcon good={i} size={30} dim={owned === 0} />
                <div className={`mt-1 text-sm font-bold tabular-nums ${owned > 0 ? "text-stone-800" : "text-stone-300"}`}>{owned}</div>
                <div className="w-full truncate text-center text-[10px] leading-tight text-stone-400">{GOODS[i].name}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* main trading column */}
      <div className="space-y-5">
          {/* contracts at-a-glance — a copy of the Goals contracts, surfaced above
              the market so you can see the target goods/markets while you trade. */}
          {(() => {
            const active = (s.quests || []).filter((q) => q && Number.isFinite(q.good));
            if (!active.length) return null;
            return (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-3" data-testid="ht-contract-banner">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-amber-700/80">📜 Contracts</span>
                  <button onClick={() => setPanel("goals")} className="text-xs text-amber-700 hover:underline" data-testid="ht-contract-goals">Goals →</button>
                </div>
                <div className="flex flex-col gap-1.5 sm:flex-row">
                  {(s.quests || []).map((q, i) => {
                    if (!q || !Number.isFinite(q.good)) return null;
                    const daysLeft = q.dueDay - s.day;
                    const pct = Math.min(100, (q.progress / q.qty) * 100);
                    const here = q.market === s.market;
                    const quickLeft = q.quickDay - s.day;
                    const deliverN = deliverableQty(s, i);
                    return (
                      <div key={i} className="min-w-0 rounded-lg bg-white/70 p-2 sm:flex-1">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span className="min-w-0 truncate font-medium text-stone-800">Deliver {q.qty} {GOODS[q.good].glyph} to {MARKETS[q.market].name}</span>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <span className={`whitespace-nowrap ${daysLeft < 0 ? "text-red-500" : "text-stone-500"}`}>{daysLeft >= 0 ? `${daysLeft}d` : "overdue"}</span>
                            <button
                              onClick={() => apply(deliverContract(s, i))}
                              title={here ? (deliverN > 0 ? `Deliver ${deliverN} from your pantry to complete` : `No ${GOODS[q.good].name} on hand`) : `Travel to ${MARKETS[q.market].name} to deliver`}
                              className={`rounded-full p-0.5 transition-colors ${here && deliverN > 0 ? "text-emerald-600 hover:bg-emerald-100" : "text-stone-300 hover:bg-stone-100"}`}
                              data-testid={`ht-complete-banner-${i}`}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-0.5 flex items-center justify-between text-[11px] text-stone-500">
                          <span>
                            reward <b className="text-amber-700">{fmt(q.reward)}</b>
                            {quickLeft >= 0 && <> · <b className="text-emerald-600">⚡+{Math.round(q.quickPct * 100)}%</b></>}
                            {here && <> · <b className="text-emerald-600">sell here!</b></>}
                          </span>
                          <span className="tabular-nums">{q.progress}/{q.qty}</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-stone-100"><div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* market */}
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <StallIcon size={48} />
                <div>
                  <div className="flex items-center gap-1.5 font-display text-lg font-semibold text-stone-800">
                    <MapPin className="h-4 w-4 text-amber-500" /> {MARKETS[s.market].name}
                  </div>
                  <div className="text-xs text-stone-500">Buy low, sell high — prices trend day to day (watch the charts).</div>
                </div>
              </div>
              <div className="flex rounded-full border border-stone-200 p-0.5 text-xs">
                {[1, 10, "max"].map((a) => (
                  <button key={a} onClick={() => setAmount(a)} data-testid={`ht-amount-${a}`} className={`rounded-full px-2.5 py-1 font-medium ${amount === a ? "bg-amber-500 text-white" : "text-stone-500 hover:text-stone-700"}`}>
                    {a === "max" ? "Max" : `×${a}`}
                  </button>
                ))}
              </div>
            </div>

            {(() => {
              const ev = marketEvents(s.day, s.seed0);
              if (!ev.active.length && !ev.upcoming.length) return null;
              return (
                <div className="mb-3 space-y-1" data-testid="ht-events">
                  {ev.active.map((e, k) => (
                    <div key={`a${k}`} className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs ${e.boom ? "bg-emerald-50 text-emerald-800" : "bg-sky-50 text-sky-800"} ${e.market === s.market ? "font-semibold ring-1 ring-inset " + (e.boom ? "ring-emerald-300" : "ring-sky-300") : ""}`}>
                      <span className="min-w-0 flex-1 truncate">{eventLabel(e)}</span>
                      <span className="whitespace-nowrap opacity-70">{e.market === s.market ? "here · " : ""}{e.daysLeft}d</span>
                    </div>
                  ))}
                  {ev.upcoming.map((e, k) => (
                    <div key={`u${k}`} className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">📣 Tomorrow — {eventLabel(e)}</div>
                  ))}
                </div>
              );
            })()}

            <div className="space-y-3">
              {MARKET_GROUPS.map((grp) => {
                const items = goods.filter((i) => GOODS[i].kind === grp.kind);
                if (!items.length) return null;
                return (
                  <div key={grp.kind}>
                    <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-600/80">{grp.label}</div>
                    <div className="divide-y divide-stone-100">
                      {items.map((i) => {
                        const price = priceAt(s.market, i, s.day, s.seed0);
                        const cls = priceClass(s.market, i, s.day, s.seed0);
                        const priceColor = cls === "cheap" ? "text-green-600" : cls === "high" ? "text-red-600" : "text-stone-700";
                        const own = s.inv[i];
                        const canBuy = Math.max(0, buyLimit(s.market, i, s.level, s.day, s.seed0, s.daySeed) - (s.bought[i] || 0));
                        const canSell = Math.max(0, sellLimit(s.market, i, s.level, s.day, s.seed0, s.daySeed) - (s.sold[i] || 0));
                        return (
                          <div key={i} className="flex items-center gap-3 py-2" data-testid={`ht-good-${i}`}>
                            <JarIcon good={i} size={30} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-stone-800">{GOODS[i].name}</div>
                              <div className="text-xs text-stone-400">Own: {own} · <span className={canBuy < 1 ? "text-red-500" : ""}>buy {canBuy}</span> · <span className={canSell < 1 ? "text-red-500" : ""}>sell {canSell}</span></div>
                            </div>
                            <div className="hidden sm:block" title="last 10 days">
                              <Sparkline data={priceHistory(s.market, i, s.day, s.seed0, 10)} />
                            </div>
                            <div className={`flex w-20 items-center justify-end gap-1 font-semibold tabular-nums ${priceColor}`}>
                              {cls === "cheap" && <TrendingDown className="h-3.5 w-3.5" />}
                              {cls === "high" && <TrendingUp className="h-3.5 w-3.5" />}
                              {fmt(price)}
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-8 px-2.5" disabled={s.cash < price || canBuy < 1} onClick={() => apply(buy(s, i, qtyFor))} data-testid={`ht-buy-${i}`}>Buy</Button>
                              <Button size="sm" variant="outline" className="h-8 px-2.5" disabled={own < 1 || canSell < 1} onClick={() => apply(sell(s, i, qtyFor))} data-testid={`ht-sell-${i}`}>Sell</Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

      </div>

      {/* left-edge management tabs (desktop only) — pinned to the left of the game
          panel, after the app's md+ sidebar (w-64). When a drawer opens they slide
          right by exactly the drawer's width (28rem) to dock flush at its edge, and
          sit above it (z-50) so you can switch sections without closing first.
          Below md the mobile horizontal tab bar takes over (see mobileTabs). */}
      <div className={`hidden md:flex fixed ${railLeft} top-28 z-50 flex-col gap-1.5 transition-transform duration-300 ease-out ${panel ? "md:translate-x-[28rem]" : "translate-x-0"}`}>
        {[{ key: "travel", icon: "🗺️", short: "Travel" }, { key: "apiary", icon: "🐝", short: "Apiary" }, { key: "workshop", icon: "🔨", short: "Craft" }, { key: "oven", icon: "🔥", short: "Oven" }, { key: "goals", icon: "🎯", short: "Goals" }, { key: "bank", icon: "🏦", short: "Bank" }, { key: "help", icon: "❓", short: "Help" }].map((p) => (
          <button key={p.key} onClick={() => setPanel(p.key)} data-testid={`ht-tab-${p.key}`} className="relative flex w-12 flex-col items-center gap-0.5 rounded-r-xl border border-l-0 border-amber-200 bg-white/95 py-2.5 text-stone-600 shadow-md hover:bg-amber-50">
            {p.key === "apiary" &&
              (() => {
                const diseased = s.hives.filter((h) => h.disaster && !h.dead).length;
                const needsTending = s.hives.some((h) => !h.dead && !h.disaster && h.lastTendDay < s.day);
                const active = s.autoDays > 0;
                const text = active ? s.autoDays : needsTending ? "!" : "✓";
                const cls = active ? "bg-emerald-500" : needsTending ? "bg-red-500" : "bg-emerald-500";
                const title = active ? `Auto-tend: ${s.autoDays} day${s.autoDays > 1 ? "s" : ""} left` : needsTending ? "Hives still need tending" : "All hives tended";
                return (
                  <span className="absolute -right-1.5 -top-1.5 flex flex-col items-end gap-0.5">
                    <span className={`grid h-4 min-w-[16px] place-items-center rounded-full px-1 text-[9px] font-bold leading-none text-white shadow ${cls}`} title={title} data-testid="ht-tab-apiary-badge">
                      {text}
                    </span>
                    {diseased > 0 && (
                      <span className="grid h-4 min-w-[16px] animate-pulse place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white shadow ring-1 ring-white" title={`${diseased} hive${diseased > 1 ? "s" : ""} in disaster — treat now!`} data-testid="ht-tab-apiary-disaster">
                        🚨
                      </span>
                    )}
                  </span>
                );
              })()}
            <span className="text-lg">{p.icon}</span>
            <span className="text-[9px] font-medium leading-none">{p.short}</span>
          </button>
        ))}
      </div>

      {/* sliding drawer for Apiary / Workshop / Oven */}
      <div className={`fixed inset-y-0 left-0 right-0 ${railLeft} z-40 overflow-hidden ${panel ? "" : "pointer-events-none"}`} aria-hidden={!panel}>
        <div className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${panel ? "opacity-100" : "opacity-0"}`} onClick={() => setPanel(null)} />
        <div className={`absolute inset-y-0 left-0 flex w-full max-w-md flex-col bg-stone-50 shadow-2xl transition-transform duration-300 ease-out ${panel ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="sticky top-0 z-10 border-b border-stone-200 bg-white px-3 py-2.5">
            {/* mobile: horizontal section tabs sit above the drawer content */}
            <div className="flex items-center gap-2 md:hidden">
              <div className="min-w-0 flex-1">{mobileTabs()}</div>
              <button onClick={() => setPanel(null)} data-testid="ht-drawer-close-mobile" className="shrink-0 rounded-full p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"><X className="h-5 w-5" /></button>
            </div>
            {/* desktop: title + close (the left rail handles switching) */}
            <div className="hidden items-center justify-between px-1 md:flex">
              <h2 className="font-display text-lg font-semibold capitalize text-stone-800">{shownPanel === "workshop" ? "Workshop" : shownPanel || ""}</h2>
              <button onClick={() => setPanel(null)} data-testid="ht-drawer-close" className="rounded-full p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"><X className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto p-3">
            {shownPanel === "travel" && travelMap}
            {shownPanel === "goals" && goalsPanel}
            {shownPanel === "bank" && bankPanel}
            {shownPanel === "help" && helpPanel}
            {shownPanel === "workshop" && (
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="flex items-center gap-1.5 font-display text-lg font-semibold text-stone-800"><Hammer className="h-4 w-4 text-amber-600" /> Workshop</h2>
              <span className="text-xs text-stone-400">🕯️ {s.inv[BEESWAX]} beeswax · 🧪 {s.inv[PROPOLIS]} propolis</span>
            </div>
            {s.level < 5 ? (
              <p className="py-2 text-sm text-stone-500">Reach <b>Level 5</b> to start crafting — a new recipe unlocks every 3 levels, all sellable at market.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {RECIPES.map((rec, ri) => {
                  const out = GOODS[rec.out];
                  const open = s.level >= rec.level;
                  const enough = rec.ins.every((ing) => s.inv[ing.good] >= ing.qty);
                  return (
                    <div key={ri} className={`flex items-center gap-2 rounded-xl border p-2 ${open ? "border-stone-200" : "border-stone-100 bg-stone-50"}`}>
                      <JarIcon good={rec.out} size={28} dim={!open} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-stone-800">{out.name}</div>
                        <div className="text-[11px] text-stone-400" title={rec.ins.map((ing) => `${ing.qty} ${GOODS[ing.good].name}`).join(" + ")}>
                          {rec.ins.map((ing, k) => (
                            <span key={k} className={s.inv[ing.good] < ing.qty ? "text-red-500" : ""}>{k > 0 ? " + " : ""}{ing.qty}{GOODS[ing.good].glyph}</span>
                          ))}
                          {" "}→ 1 · ~{fmt(out.base)}
                        </div>
                      </div>
                      {open ? (
                        <Button size="sm" variant="outline" className="h-8 px-2.5" disabled={!enough} onClick={() => apply(craft(s, ri, amount === "max" ? 1e9 : amount))} data-testid={`ht-craft-${ri}`}>Craft</Button>
                      ) : (
                        <span className="rounded bg-stone-100 px-2 py-1 text-[11px] font-medium text-stone-400">Lv {rec.level}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

            )}
            {shownPanel === "oven" && (
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="flex items-center gap-1.5 font-display text-lg font-semibold text-stone-800">🔥 Oven</h2>
              {s.ovenSlots >= 1 && <span className="text-xs text-stone-400">{s.ovenJobs.length}/{s.ovenSlots} baking · batch {s.ovenBatch}</span>}
            </div>

            {s.ovenSlots < 1 ? (
              s.level < 10 ? (
                <p className="py-2 text-sm text-stone-500">Reach <b>Level 10</b> to buy a stove/oven and bake cakes, candy, cookies, muffins, lavender lemonade and mead.</p>
              ) : (
                <div className="flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/50 p-3">
                  <p className="text-sm text-stone-600">Buy a stove/oven to bake high-value treats. You can add another oven every 5 levels (Lv 15, 20, 25…).</p>
                  <Button size="sm" disabled={s.cash < OVEN_COST} onClick={() => apply(buyOven(s))} data-testid="ht-buy-oven">Buy {fmt(OVEN_COST)}</Button>
                </div>
              )
            ) : (
              <>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {(() => {
                    const atMax = s.ovenSlots >= OVEN_MAX_SLOTS;
                    const levelLocked = !atMax && s.ovenSlots >= ovenSlotsAllowed(s.level);
                    const cost = ovenSlotUpgradeCost(s.ovenSlots);
                    return (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={atMax || levelLocked || s.cash < cost} onClick={() => apply(upgradeOvenSlots(s))} data-testid="ht-oven-slots">
                        {atMax ? "Ovens maxed" : levelLocked ? `+ Oven at Lv ${ovenSlotUnlockLevel(s.ovenSlots + 1)}` : `+ Oven (${s.ovenSlots + 1}) · ${fmt(cost)}`}
                      </Button>
                    );
                  })()}
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={s.ovenBatch >= OVEN_MAX_BATCH || s.cash < OVEN_BATCH_COST} onClick={() => apply(upgradeOvenBatch(s))} data-testid="ht-oven-batch">
                    {s.ovenBatch >= OVEN_MAX_BATCH ? "Batch maxed" : `+ Batch (${s.ovenBatch + 1}) · ${fmt(OVEN_BATCH_COST)}`}
                  </Button>
                </div>
                {/* Oven bank — one rounded tile per oven, laid out horizontally.
                    Baking tiles show the item + time left; owned-but-idle tiles are
                    empty; ovens you haven't bought yet are greyed out. */}
                <div className="mb-3 flex flex-wrap gap-2" data-testid="ht-oven-bank">
                  {Array.from({ length: Math.max(s.ovenSlots, ovenSlotsAllowed(s.level), 1) }).map((_, i) => {
                    const job = i < s.ovenJobs.length ? s.ovenJobs[i] : null;
                    const owned = i < s.ovenSlots;
                    if (job) {
                      return (
                        <div key={i} className="flex h-[64px] w-[64px] flex-col items-center justify-center rounded-xl border border-amber-300 bg-amber-50 shadow-sm" title={`Baking ${job.qty} ${GOODS[job.good].name}`} data-testid={`ht-oven-slot-${i}`}>
                          <JarIcon good={job.good} size={26} />
                          <span className="mt-0.5 text-[10px] font-semibold tabular-nums leading-none text-amber-700">{job.daysLeft}d left</span>
                        </div>
                      );
                    }
                    if (owned) {
                      return (
                        <div key={i} className="flex h-[64px] w-[64px] flex-col items-center justify-center rounded-xl border border-stone-200 bg-stone-50 text-stone-400" title="Idle oven — ready to bake" data-testid={`ht-oven-slot-${i}`}>
                          <span className="text-xl leading-none opacity-40">🔥</span>
                          <span className="mt-0.5 text-[9px] font-medium leading-none">Idle</span>
                        </div>
                      );
                    }
                    return (
                      <div key={i} className="flex h-[64px] w-[64px] flex-col items-center justify-center rounded-xl border border-dashed border-stone-200 bg-stone-100/60 text-stone-300" title="Oven not purchased yet" data-testid={`ht-oven-slot-${i}`}>
                        <span className="text-lg leading-none opacity-50">🔒</span>
                        <span className="mt-0.5 text-[9px] font-medium leading-none">Locked</span>
                      </div>
                    );
                  })}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {OVEN_ITEMS.map((rec, oi) => {
                    const out = GOODS[rec.out];
                    const enough = rec.ins.every((ing) => (s.inv[ing.good] || 0) >= ing.qty * s.ovenBatch);
                    const ingredients = rec.ins.map((ing) => `${ing.qty * s.ovenBatch}${GOODS[ing.good].glyph}`).join(" + ");
                    const slotFree = s.ovenJobs.length < s.ovenSlots;
                    return (
                      <div key={oi} className="flex items-center gap-2 rounded-xl border border-stone-200 p-2">
                        <JarIcon good={rec.out} size={28} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-stone-800">{out.name}</div>
                          <div className="text-[11px] text-stone-400">{ingredients} → {s.ovenBatch} · {rec.bake}d · ~{fmt(out.base)}</div>
                        </div>
                        <Button size="sm" variant="outline" className="h-8 px-2.5" disabled={!slotFree || !enough} onClick={() => apply(startBake(s, oi))} data-testid={`ht-bake-${oi}`}>Bake</Button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
            )}
            {shownPanel === "apiary" && (
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-stone-800">Apiary 🐝</h2>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-8 px-2" title="Collect from every hive (5% handling fee)" onClick={() => apply(collectAll(s))}>Collect all</Button>
                <Button size="sm" variant="outline" className="h-8 px-2" title="Tend every hive (5% surcharge)" onClick={() => apply(tendAll(s))}>Tend all</Button>
              </div>
            </div>

            {s.level >= AUTO_LEVEL && (
              <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50/60 p-2.5">
                <div className="mb-1.5 text-xs font-medium text-amber-800">🤖 Auto-tend &amp; collect{s.autoDays > 0 ? ` · ${s.autoDays} day${s.autoDays > 1 ? "s" : ""} left` : " (tends & collects every hive each day)"}</div>
                <div className="flex flex-wrap gap-1.5">
                  {AUTO_PLANS.map((pl, i) => (
                    <Button key={i} size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={s.cash < pl.cost} onClick={() => apply(buyAuto(s, i))} data-testid={`ht-auto-${pl.days}`}>+{pl.days}d · {fmt(pl.cost)}</Button>
                  ))}
                </div>
              </div>
            )}

            {anyLowHive && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">⚠️ A hive is weak — tend it or it'll be abandoned!</div>}

            <div className="space-y-3">
              {s.hives.length === 0 && <p className="py-4 text-center text-sm text-stone-400">No hives yet — buy one below.</p>}
              {s.hives.map((h) => {
                const g = GOODS[h.good];
                const cost = upgradeCost(h);
                const hColor = h.health >= 60 ? "bg-green-500" : h.health >= 30 ? "bg-amber-500" : "bg-red-500";
                const tendedToday = h.lastTendDay >= s.day;
                const diseased = !!h.disaster && !h.dead;
                const instant = diseased ? instantCost(h) : 0;
                const instantFull = diseased ? instantCostFull(h) : 0;
                const treatNow = diseased ? treatCost(h) : 0;
                const treatFull = diseased ? treatCostFull(h) : 0;
                const discounted = diseased && !!h.insured; // insured → 90% off recovery
                return (
                  <div key={h.id} className={`rounded-xl border p-3 ${h.dead ? "border-stone-200 bg-stone-100/70 opacity-70" : diseased ? "border-red-200 bg-red-50/60" : "border-stone-100 bg-stone-50/60"}`} data-testid={`ht-hive-${h.id}`}>
                    <div className="flex items-center gap-3">
                      <HiveIcon kind={h.kind} health={h.dead || diseased ? 0 : h.health} size={44} />
                      <div className="min-w-0 flex-1">
                        {editing === h.id ? (
                          <input
                            autoFocus
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={() => commitRename(h.id)}
                            onKeyDown={(e) => { if (e.key === "Enter") commitRename(h.id); if (e.key === "Escape") setEditing(null); }}
                            maxLength={24}
                            className="w-full rounded border border-amber-300 px-1.5 py-0.5 text-sm outline-none"
                            data-testid={`ht-rename-${h.id}`}
                          />
                        ) : (
                          <button className="group flex items-center gap-1 text-sm font-semibold text-stone-800" onClick={() => { setEditing(h.id); setEditName(h.name); }}>
                            {h.kind === "special" && <Sparkles className="h-3.5 w-3.5 text-pink-500" />}
                            {h.name}
                            <Pencil className="h-3 w-3 text-stone-300 group-hover:text-amber-500" />
                          </button>
                        )}
                        <div className="text-xs text-stone-500">makes {g.glyph} {g.name} · {h.honeyEvery > 1 ? `+${h.perTend} / ${h.honeyEvery} tends` : `+${h.perTend}/tend`}</div>
                        {h.dead ? (
                          <>
                            <div className="mt-0.5 text-xs font-medium text-stone-500">💀 Lost to {h.disaster} — permanently unproductive</div>
                            {h.insPayout > 0 && (
                              <div className="mt-0.5 text-xs font-semibold text-emerald-700">🛡️ Insurance payout {fmt(h.insPayout)} — collected when you remove this hive</div>
                            )}
                          </>
                        ) : diseased ? (
                          <div className="mt-0.5 text-xs font-semibold text-red-700">🦠 {h.disaster} · {h.disDaysLeft} day{h.disDaysLeft > 1 ? "s" : ""} of freeze left</div>
                        ) : (
                          <>
                            <div className="mt-0.5 text-xs tabular-nums text-stone-600">🍯 {h.honey}/{h.cap} · 🕯️ {h.beeswax} · 🧪 {h.propolis} · 🧇 {h.honeycomb || 0}</div>
                            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-stone-200"><div className={`h-full ${hColor} transition-all`} style={{ width: `${h.health}%` }} /></div>
                          </>
                        )}
                      </div>
                    </div>
                    {!h.dead && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="text-[11px] text-stone-500" title="Queen traits">👑 {Math.round((h.queen?.prod || 1) * 100)}% honey · {Math.round((h.queen?.resist || 1) * 100)}% resist</span>
                        <button className="text-[11px] text-purple-600 hover:underline disabled:opacity-40" disabled={s.cash < requeenCost(h)} onClick={() => apply(requeen(s, h.id))} data-testid={`ht-requeen-${h.id}`}>Requeen {fmt(requeenCost(h))}</button>
                        {h.insured ? (
                          <span className="text-[11px] font-medium text-emerald-600" title={`Pays ${fmt(insurancePayout(h))} if lost · 90% off recovery`}>🛡️ Insured · pays {fmt(insurancePayout(h))}</span>
                        ) : diseased ? (
                          <span className="text-[11px] font-medium text-stone-400" title="A hive can't be insured while a disaster is active">🛡️ Can't insure mid-disaster</span>
                        ) : (
                          <button className="text-[11px] text-sky-600 hover:underline disabled:opacity-40" disabled={s.cash < insuranceCost(h)} onClick={() => apply(insureHive(s, h.id))} data-testid={`ht-insure-${h.id}`} title={`Pays ${fmt(insurancePayout(h))} if lost · 90% off recovery`}>🛡️ Insure {fmt(insuranceCost(h))}</button>
                        )}
                      </div>
                    )}
                    {diseased && (
                      <div className="mt-2 rounded-lg bg-red-100/70 px-2.5 py-2 text-xs leading-snug text-red-800">
                        {disasterStory(h.disaster)} Treat for{" "}
                        {discounted && <s className="text-red-400">{fmt(treatFull)}</s>} <b>{fmt(treatNow)}/day</b> ({h.disDaysLeft} day{h.disDaysLeft > 1 ? "s" : ""}), or resolve now for{" "}
                        {discounted && <s className="text-red-400">{fmt(instantFull)}</s>} <b>{fmt(instant)}</b>. Skip a day and the hive is lost.
                        {discounted && <span className="mt-1 block font-semibold text-emerald-700">🛡️ Insured — 90% off recovery.</span>}
                      </div>
                    )}
                    {h.dead ? (
                      <div className="mt-2 flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 flex-1 px-1 text-xs" onClick={() => apply(removeHive(s, h.id))} data-testid={`ht-remove-${h.id}`}>
                          {h.insPayout > 0 ? `Remove & claim ${fmt(h.insPayout)}` : "Remove lost hive"}
                        </Button>
                      </div>
                    ) : diseased ? (
                      <div className="mt-2 flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 flex-1 px-1 text-xs" disabled={h.disTreated || s.cash < treatNow} onClick={() => apply(treatDisaster(s, h.id))} data-testid={`ht-treat-${h.id}`}>{h.disTreated ? "Treated ✓" : `Treat ${fmt(treatNow)}`}</Button>
                        <Button size="sm" variant="outline" className="h-7 flex-1 px-1 text-xs" disabled={s.cash < instant} onClick={() => apply(resolveDisaster(s, h.id))} data-testid={`ht-resolve-${h.id}`}>Resolve {fmt(instant)}</Button>
                      </div>
                    ) : (
                      <div className="mt-2 flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 flex-1 px-1 text-xs" disabled={tendedToday || s.cash < h.tendCost} onClick={() => apply(tend(s, h.id))} data-testid={`ht-tend-${h.id}`}>{tendedToday ? "Tended ✓" : `Tend ${fmt(h.tendCost)}`}</Button>
                        <Button size="sm" variant="outline" className="h-7 flex-1 px-1 text-xs" disabled={h.honey + h.beeswax + h.propolis + (h.honeycomb || 0) < 1} onClick={() => apply(collect(s, h.id))}>Collect</Button>
                        <Button size="sm" variant="outline" className="h-7 flex-1 px-1 text-xs" disabled={s.cash < cost} onClick={() => apply(upgradeHive(s, h.id))}>Up {fmt(cost)}</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-stone-400"><Plus className="h-3.5 w-3.5" /> Buy a hive</div>
              <div className="grid grid-cols-1 gap-1.5">
                {HIVE_TYPES.map((t, ti) => (
                  <Button key={ti} variant="outline" className="h-auto justify-between py-1.5" disabled={s.cash < t.cost} onClick={() => apply(buyHive(s, ti))} data-testid={`ht-buy-hive-${ti}`}>
                    <span className="flex items-center gap-1.5 text-sm">
                      {t.kind === "special" && <Sparkles className="h-3.5 w-3.5 text-pink-500" />}
                      <span>{GOODS[t.good].glyph}</span>
                      {shortName(t.good)} Hive
                      {t.every > 1 && <span className="text-[10px] font-normal text-stone-400">1/{t.every} tends</span>}
                    </span>
                    <span className="tabular-nums text-stone-500">{fmt(t.cost)}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
            )}
          </div>
        </div>
      </div>

      {/* guided first-days tutorial coach */}
      {s.tut >= 0 && s.tut < TUTORIAL.length && (
        <div className="fixed bottom-4 left-1/2 z-30 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-2xl border border-amber-300 bg-white p-4 shadow-2xl" data-testid="ht-tutorial">
          <div className="flex items-start gap-3">
            <div className="text-2xl">{TUTORIAL[s.tut].icon}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <div className="font-display font-semibold text-stone-800">{TUTORIAL[s.tut].title}</div>
                <span className="shrink-0 text-[11px] text-stone-400">Step {s.tut + 1}/{TUTORIAL.length}</span>
              </div>
              <p className="mt-0.5 text-sm text-stone-600">{TUTORIAL[s.tut].body}</p>
              <div className="mt-2 flex items-center gap-3">
                {TUTORIAL[s.tut].done ? (
                  <span className="text-xs font-medium text-amber-600">Try it to continue…</span>
                ) : (
                  <Button size="sm" onClick={() => setTut(s.tut + 1)} data-testid="ht-tut-next">{s.tut === TUTORIAL.length - 1 ? "Finish" : "Next"}</Button>
                )}
                <button className="text-xs text-stone-400 hover:text-stone-600" onClick={() => setTut(-1)} data-testid="ht-tut-skip">Skip tutorial</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
